import React, { useState, useEffect } from 'react';
import { getUserConversations, getConversationMessages, getAllConversations, getConversationRejectionStatus, deleteConversationMessage } from '../services/api';
import MessageRenderer from '../components/chat/MessageRenderer';
import CitationTooltip from '../components/chat/CitationTooltip';
import { useCitationTooltip } from '../components/chat/useCitationTooltip';
import { learnedQAService } from '../services/learnedQAService';
import QAReviewCard from '../components/learned_qa/QAReviewCard';
import QAReviewModal from '../components/learned_qa/QAReviewModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { Tooltip } from '../components/common';
import '../styles/chatHistory.css';

// User interface for API response - keeping for type safety

interface Conversation {
  conversation_id: string;
  user_id: string;
  user_name?: string; // For internal users
  message_count: number;
  last_activity: string;
  first_activity: string;
  feedback_count: number;
  conversation_title: string;
  is_guest: boolean;
}

interface ChatMessage {
  message_id: string;
  conversation_id: string;
  user_id: string;
  user_message: string;
  assistant_response: string;
  created_at: string;
  feedback_type: string | null;
  feedback_reason: string | null;
  feedback_comments: string | null;
  feedback_created_at: string | null;
  metadata: any;
  is_deleted?: boolean;
  deleted_at?: string;
}

interface GroupedConversations {
  [key: string]: {
    user_id: string;
    user_name: string;
    display_name: string;
    is_guest: boolean;
    conversations: Conversation[];
    total_messages: number;
    total_feedback: number;
    last_activity: string;
    preferences?: any; // User preferences data
  };
}

interface UserPreferencesTooltip {
  visible: boolean;
  x: number;
  y: number;
  preferences: any;
}


const ChatHistory: React.FC = () => {
  const clientId = localStorage.getItem('client_id');
  const [groupedConversations, setGroupedConversations] = useState<GroupedConversations>({});
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [collapsedBlocks, setCollapsedBlocks] = useState<Set<string>>(new Set());
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [preferencesTooltip, setPreferencesTooltip] = useState<UserPreferencesTooltip>({
    visible: false,
    x: 0,
    y: 0,
    preferences: null
  });
  const tooltipTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  
  // Delete confirmation state
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    visible: boolean;
    messageId: string | null;
    conversationId: string | null;
    userId: string | null;
  }>({
    visible: false,
    messageId: null,
    conversationId: null,
    userId: null
  });

  // Use shared citation tooltip hook - pass collapsedBlocks to trigger re-initialization
  const { citationTooltip, tooltipEventHandlers } = useCitationTooltip(messages, collapsedBlocks);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [feedbackFilter, setFeedbackFilter] = useState<'all' | 'thumbs_up' | 'thumbs_down' | 'need_review' | 'no_feedback'>('all');
  const [userTypeFilter, setUserTypeFilter] = useState<'all' | 'guest' | 'internal'>('all');
  const [sortBy, setSortBy] = useState<'last_activity' | 'message_count' | 'feedback_count'>('last_activity');
  
  // Pagination state
  const [currentOffset, setCurrentOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const limit = 50;

  // Q&A Review state
  const [qaCandidates, setQaCandidates] = useState<any[]>([]);
  const [loadingQACandidates, setLoadingQACandidates] = useState(false);
  const [qaFilter, setQaFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
  const [selectedQACandidate, setSelectedQACandidate] = useState<any | null>(null);
  const [showQAReviewModal, setShowQAReviewModal] = useState(false);
  
  // Message-level Q&A candidates (Map<messageId, candidate>)
  const [messageQACandidates, setMessageQACandidates] = useState<Map<string, any>>(new Map());
  const [loadingMessageQAs, setLoadingMessageQAs] = useState<Set<string>>(new Set());
  
  // Extract Q&A loading states
  const [extractingFromConversation, setExtractingFromConversation] = useState(false);
  const [extractingFromMessage, setExtractingFromMessage] = useState<Set<string>>(new Set());
  
  // Q&A status counts per conversation (Map<conversationId, {pending, approved, rejected}>)
  const [qaStatusCounts, setQaStatusCounts] = useState<Map<string, {pending: number, approved: number, rejected: number}>>(new Map());
  
  // Conversation rejection status (Map<conversationId, {rejected: boolean, rejection_reason: string | null, rejected_by: string | null, rejected_at: string | null}>)
  const [conversationRejections, setConversationRejections] = useState<Map<string, {rejected: boolean, rejection_reason: string | null, rejected_by: string | null, rejected_at: string | null}>>(new Map());

  // Fetch conversations with pagination
  const fetchConversations = async (offset: number = 0, append: boolean = false) => {
    if (!clientId) return;
    
    try {
      if (!append) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      // Use the paginated endpoint
      const response = await getAllConversations(clientId, offset, limit);
      const conversations = response.conversations || [];
      
      // Group conversations by user
      const grouped: GroupedConversations = conversations.reduce((acc: GroupedConversations, conv: any) => {
        const userId = conv.user_id;
        
        if (!acc[userId]) {
          const isGuest = conv.is_guest;
          const displayName = conv.user_name && conv.user_name.trim() !== '' 
            ? conv.user_name 
            : isGuest 
              ? `Guest ${userId.replace('guest_', '').substring(0, 8)}...`
              : userId || 'Unknown User';
              
          acc[userId] = {
            user_id: userId,
            user_name: conv.user_name || '',
            display_name: displayName,
            is_guest: isGuest,
            conversations: [],
            total_messages: 0,
            total_feedback: 0,
            last_activity: conv.last_activity,
            preferences: conv.preferences
          };
        }
        
        acc[userId].conversations.push(conv);
        acc[userId].total_messages += conv.message_count || 0;
        acc[userId].total_feedback += conv.feedback_count || 0;
        
        // Update last activity if this conversation is more recent
        if (new Date(conv.last_activity) > new Date(acc[userId].last_activity)) {
          acc[userId].last_activity = conv.last_activity;
        }
        
        return acc;
      }, {});
      
      // Sort conversations within each user group by last_activity (most recent first)
      Object.values(grouped).forEach((group) => {
        group.conversations.sort((a: Conversation, b: Conversation) => 
          new Date(b.last_activity).getTime() - new Date(a.last_activity).getTime()
        );
      });
      
      if (append) {
        // Merge with existing conversations
        setGroupedConversations(prev => {
          const merged = { ...prev };
          (Object.entries(grouped) as [string, GroupedConversations[string]][]).forEach(([userId, newGroup]) => {
            if (merged[userId]) {
              // Merge conversations
              merged[userId].conversations.push(...newGroup.conversations);
              merged[userId].total_messages += newGroup.total_messages;
              merged[userId].total_feedback += newGroup.total_feedback;
              
              // Update last activity
              if (new Date(newGroup.last_activity) > new Date(merged[userId].last_activity)) {
                merged[userId].last_activity = newGroup.last_activity;
              }
              
              // Re-sort conversations
              merged[userId].conversations.sort((a: Conversation, b: Conversation) => 
                new Date(b.last_activity).getTime() - new Date(a.last_activity).getTime()
              );
            } else {
              merged[userId] = newGroup;
            }
          });
          return merged;
        });
      } else {
        setGroupedConversations(grouped);
      }
      
      // Update pagination state
      setHasMore(response.has_more || false);
      setCurrentOffset(offset + conversations.length);
      
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setError('Failed to fetch conversations');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchConversations(0);
  }, [clientId]);

  // Auto-select first conversation when conversations are loaded (only on initial load)
  useEffect(() => {
    if (!selectedConversation && Object.keys(groupedConversations).length > 0 && !loading) {
      // Find the first conversation from the first user group
      // Conversations are already sorted by last_activity (most recent first)
      const firstUserId = Object.keys(groupedConversations)[0];
      const firstUserGroup = groupedConversations[firstUserId];
      if (firstUserGroup && firstUserGroup.conversations.length > 0) {
        // Select the first conversation (most recent)
        setSelectedConversation(firstUserGroup.conversations[0]);
      }
    }
  }, [groupedConversations, selectedConversation, loading]);

  // Fetch messages for selected conversation
  useEffect(() => {
    const fetchMessages = async () => {
      if (!clientId || !selectedConversation) return;
      
      try {
        const response = await getConversationMessages(
          clientId, 
          selectedConversation.user_id, 
          selectedConversation.conversation_id
        );
        setMessages(response.messages || []);
      } catch (error) {
        console.error('Error fetching messages:', error);
        setError('Failed to fetch messages');
      }
    };

    fetchMessages();
  }, [clientId, selectedConversation]);

  // Fetch Q&A status counts for all conversations
  useEffect(() => {
    const fetchQAStatusCounts = async () => {
      if (!clientId || Object.keys(groupedConversations).length === 0) return;

      try {
        // Get all candidates
        const allCandidates = await learnedQAService.getCandidates(clientId, 1000, 0);
        
        // Group by conversation_id and count statuses
        const statusCountsMap = new Map<string, {pending: number, approved: number, rejected: number}>();
        
        allCandidates.forEach((candidate: any) => {
          const convId = candidate.conversation_id;
          if (!statusCountsMap.has(convId)) {
            statusCountsMap.set(convId, { pending: 0, approved: 0, rejected: 0 });
          }
          const counts = statusCountsMap.get(convId)!;
          // Only count pending candidates that are not in training
          if (candidate.status === 'pending' && 
              (!candidate.training_status || candidate.training_status === 'removed')) {
            counts.pending++;
          } else if (candidate.status === 'approved') {
            counts.approved++;
          } else if (candidate.status === 'rejected') {
            counts.rejected++;
          }
        });
        
        setQaStatusCounts(statusCountsMap);
      } catch (error) {
        console.error('Error fetching Q&A status counts:', error);
        // Don't show error toast - this is optional functionality
      }
    };

    fetchQAStatusCounts();
    
    // Fetch rejection status for all conversations
    const fetchRejectionStatuses = async () => {
      if (!clientId || Object.keys(groupedConversations).length === 0) return;
      
      const rejectionMap = new Map<string, {rejected: boolean, rejection_reason: string | null, rejected_by: string | null, rejected_at: string | null}>();
      
      // Get all conversation IDs from grouped conversations
      const allConversationIds: string[] = [];
      Object.values(groupedConversations).forEach(userGroup => {
        userGroup.conversations.forEach(conv => {
          allConversationIds.push(conv.conversation_id);
        });
      });
      
      // Fetch rejection status for each conversation
      const rejectionPromises = allConversationIds.map(async (convId) => {
        try {
          const status = await getConversationRejectionStatus(clientId, convId);
          rejectionMap.set(convId, {
            rejected: status.rejected || false,
            rejection_reason: status.rejection_reason || null,
            rejected_by: status.rejected_by || null,
            rejected_at: status.rejected_at || null
          });
        } catch (error) {
          console.error(`Error fetching rejection status for conversation ${convId}:`, error);
          rejectionMap.set(convId, {
            rejected: false,
            rejection_reason: null,
            rejected_by: null,
            rejected_at: null
          });
        }
      });
      
      await Promise.all(rejectionPromises);
      setConversationRejections(rejectionMap);
    };
    
    fetchRejectionStatuses();
  }, [clientId, groupedConversations]);

  // Fetch Q&A candidates for selected conversation
  useEffect(() => {
    const fetchQACandidates = async () => {
      if (!clientId || !selectedConversation) {
        setQaCandidates([]);
        return;
      }
      
      // Only fetch if conversation has feedback
      if (selectedConversation.feedback_count === 0) {
        setQaCandidates([]);
        return;
      }

      try {
        setLoadingQACandidates(true);
        const candidates = await learnedQAService.getCandidates(clientId, 1000, 0);
        // Filter candidates for this conversation
        const conversationCandidates = candidates.filter(
          (c: any) => c.conversation_id === selectedConversation.conversation_id
        );
        setQaCandidates(conversationCandidates);
      } catch (error) {
        console.error('Error fetching Q&A candidates:', error);
        // Don't show error toast - this is optional functionality
      } finally {
        setLoadingQACandidates(false);
      }
    };

    fetchQACandidates();
  }, [clientId, selectedConversation]);

  // Fetch Q&A candidates for messages with feedback
  useEffect(() => {
    const fetchMessageQACandidates = async () => {
      if (!clientId || !selectedConversation || !messages.length) {
        setMessageQACandidates(new Map());
        setLoadingMessageQAs(new Set());
        return;
      }

      // Find messages with feedback (all types: thumbs_up, thumbs_down, need_review)
      const messagesWithFeedback = messages.filter(
        (msg) => msg.feedback_type && ['thumbs_up', 'thumbs_down', 'need_review'].includes(msg.feedback_type)
      );

      if (messagesWithFeedback.length === 0) {
        setMessageQACandidates(new Map());
        setLoadingMessageQAs(new Set());
        return;
      }

      // Set loading state for all messages
      const loadingSet = new Set(messagesWithFeedback.map(msg => msg.message_id));
      setLoadingMessageQAs(loadingSet);

      // Fetch candidates for each message
      const newMap = new Map<string, any>();

      for (const msg of messagesWithFeedback) {
        try {
          const candidate = await learnedQAService.getCandidateByMessageId(
            clientId,
            msg.message_id,
            msg.conversation_id
          );
          if (candidate) {
            newMap.set(msg.message_id, candidate);
          }
        } catch (error) {
          console.error(`Error fetching Q&A candidate for message ${msg.message_id}:`, error);
        } finally {
          loadingSet.delete(msg.message_id);
          setLoadingMessageQAs(new Set(loadingSet));
        }
      }

      setMessageQACandidates(newMap);
      setLoadingMessageQAs(new Set());
    };

    fetchMessageQACandidates();
  }, [clientId, selectedConversation, messages]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const copyToClipboard = async (text: string, messageId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const showPreferencesTooltip = (event: React.MouseEvent, preferences: any) => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    setPreferencesTooltip({
      visible: true,
      x: rect.right + 5,
      y: rect.top,
      preferences
    });
  };

  const hidePreferencesTooltip = () => {
    tooltipTimeoutRef.current = setTimeout(() => {
      setPreferencesTooltip(prev => ({ ...prev, visible: false }));
    }, 200);
  };
  
  const cancelHideTooltip = () => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
  };

  const getFeedbackIcon = (feedbackType: string | null) => {
    switch (feedbackType) {
      case 'thumbs_up':
        return '👍';
      case 'thumbs_down':
        return '👎';
      case 'need_review':
        return '⚠️';
      default:
        return null;
    }
  };

  // Q&A Review handlers
  const handleApproveQA = async (candidateId: string, notes?: string) => {
    if (!clientId) return;
    
    try {
      const reviewerId = localStorage.getItem('user_id') || 'system';
      await learnedQAService.approveCandidate(clientId, candidateId, reviewerId, notes);
      toast.success('Q&A candidate approved');
      // Reload candidates to get updated statuses
      const candidates = await learnedQAService.getCandidates(clientId, 1000, 0);
      if (selectedConversation) {
        const conversationCandidates = candidates.filter(
          (c: any) => c.conversation_id === selectedConversation.conversation_id
        );
        setQaCandidates(conversationCandidates);
        
        // Also update message-level candidates
        const updatedMap = new Map(messageQACandidates);
        conversationCandidates.forEach((candidate: any) => {
          const sourceMsgId = typeof candidate.context === 'object' && candidate.context 
            ? (candidate.context.source_message_id || candidate.context.message_id)
            : null;
          if (sourceMsgId) {
            updatedMap.set(String(sourceMsgId), candidate);
          }
        });
        setMessageQACandidates(updatedMap);
      }
      
      // Update status counts
      const statusCountsMap = new Map<string, {pending: number, approved: number, rejected: number}>();
      candidates.forEach((candidate: any) => {
        const convId = candidate.conversation_id;
        if (!statusCountsMap.has(convId)) {
          statusCountsMap.set(convId, { pending: 0, approved: 0, rejected: 0 });
        }
        const counts = statusCountsMap.get(convId)!;
        if (candidate.status === 'pending') counts.pending++;
        else if (candidate.status === 'approved') counts.approved++;
        else if (candidate.status === 'rejected') counts.rejected++;
      });
      setQaStatusCounts(statusCountsMap);
      
      // Refresh message-level candidates
      const updatedMap = new Map(messageQACandidates);
      Array.from(updatedMap.entries()).forEach(([messageId, candidate]) => {
        if (candidate.id === candidateId) {
          learnedQAService.getCandidate(clientId, candidateId).then((updated) => {
            if (updated) {
              updatedMap.set(messageId, updated);
              setMessageQACandidates(new Map(updatedMap));
            }
          }).catch((err) => {
            console.error('Error refreshing message candidate:', err);
          });
        }
      });
      setSelectedQACandidate(null);
      
      // Refresh Q&A candidates list
      if (selectedConversation) {
        try {
          const refreshedCandidates = await learnedQAService.getCandidates(clientId, 100, 0);
          const conversationCandidates = refreshedCandidates.filter(
            (c: any) => c.conversation_id === selectedConversation.conversation_id
          );
          setQaCandidates(conversationCandidates);
        } catch (err) {
          console.error('Error refreshing candidates:', err);
        }
      }
    } catch (error: any) {
      console.error('Error approving candidate:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to approve candidate';
      toast.error(typeof errorMessage === 'string' ? errorMessage : 'Failed to approve candidate');
    }
  };

  const handleRejectQA = async (candidateId: string, reason?: string) => {
    if (!clientId) return;
    
    try {
      const reviewerId = localStorage.getItem('user_id') || 'system';
      await learnedQAService.rejectCandidate(clientId, candidateId, reviewerId, reason);
      toast.success('Q&A candidate rejected');
      // Reload candidates to get updated statuses
      const candidates = await learnedQAService.getCandidates(clientId, 1000, 0);
      if (selectedConversation) {
        const conversationCandidates = candidates.filter(
          (c: any) => c.conversation_id === selectedConversation.conversation_id
        );
        setQaCandidates(conversationCandidates);
        
        // Also update message-level candidates
        const updatedMap = new Map(messageQACandidates);
        conversationCandidates.forEach((candidate: any) => {
          const sourceMsgId = typeof candidate.context === 'object' && candidate.context 
            ? (candidate.context.source_message_id || candidate.context.message_id)
            : null;
          if (sourceMsgId) {
            updatedMap.set(String(sourceMsgId), candidate);
          }
        });
        setMessageQACandidates(updatedMap);
      }
      
      // Update status counts
      const statusCountsMap = new Map<string, {pending: number, approved: number, rejected: number}>();
      candidates.forEach((candidate: any) => {
        const convId = candidate.conversation_id;
        if (!statusCountsMap.has(convId)) {
          statusCountsMap.set(convId, { pending: 0, approved: 0, rejected: 0 });
        }
        const counts = statusCountsMap.get(convId)!;
        if (candidate.status === 'pending') counts.pending++;
        else if (candidate.status === 'approved') counts.approved++;
        else if (candidate.status === 'rejected') counts.rejected++;
      });
      setQaStatusCounts(statusCountsMap);
      
      // Refresh message-level candidates
      const updatedMap = new Map(messageQACandidates);
      Array.from(updatedMap.entries()).forEach(([messageId, candidate]) => {
        if (candidate.id === candidateId) {
          learnedQAService.getCandidate(clientId, candidateId).then((updated) => {
            if (updated) {
              updatedMap.set(messageId, updated);
              setMessageQACandidates(new Map(updatedMap));
            }
          }).catch((err) => {
            console.error('Error refreshing message candidate:', err);
          });
        }
      });
      setSelectedQACandidate(null);
      
      // Refresh Q&A candidates list
      if (selectedConversation) {
        try {
          const refreshedCandidates = await learnedQAService.getCandidates(clientId, 100, 0);
          const conversationCandidates = refreshedCandidates.filter(
            (c: any) => c.conversation_id === selectedConversation.conversation_id
          );
          setQaCandidates(conversationCandidates);
        } catch (err) {
          console.error('Error refreshing candidates:', err);
        }
      }
    } catch (error) {
      console.error('Error rejecting candidate:', error);
      toast.error('Failed to reject candidate');
    }
  };

  const handleEditAndApproveQA = async (
    candidateId: string,
    editedQuestion?: string,
    editedAnswer?: string,
    notes?: string
  ) => {
    if (!clientId) return;
    
    try {
      const reviewerId = localStorage.getItem('user_id') || 'system';
      await learnedQAService.editAndApproveCandidate(
        clientId,
        candidateId,
        reviewerId,
        editedQuestion,
        editedAnswer,
        notes
      );
      toast.success('Q&A candidate edited and approved');
      // Reload candidates to get updated statuses
      const candidates = await learnedQAService.getCandidates(clientId, 1000, 0);
      if (selectedConversation) {
        const conversationCandidates = candidates.filter(
          (c: any) => c.conversation_id === selectedConversation.conversation_id
        );
        setQaCandidates(conversationCandidates);
        
        // Also update message-level candidates
        const updatedMap = new Map(messageQACandidates);
        conversationCandidates.forEach((candidate: any) => {
          const sourceMsgId = typeof candidate.context === 'object' && candidate.context 
            ? (candidate.context.source_message_id || candidate.context.message_id)
            : null;
          if (sourceMsgId) {
            updatedMap.set(String(sourceMsgId), candidate);
          }
        });
        setMessageQACandidates(updatedMap);
      }
      
      // Update status counts
      const statusCountsMap = new Map<string, {pending: number, approved: number, rejected: number}>();
      candidates.forEach((candidate: any) => {
        const convId = candidate.conversation_id;
        if (!statusCountsMap.has(convId)) {
          statusCountsMap.set(convId, { pending: 0, approved: 0, rejected: 0 });
        }
        const counts = statusCountsMap.get(convId)!;
        if (candidate.status === 'pending') counts.pending++;
        else if (candidate.status === 'approved') counts.approved++;
        else if (candidate.status === 'rejected') counts.rejected++;
      });
      setQaStatusCounts(statusCountsMap);
      
      // Refresh message-level candidates
      const updatedMap = new Map(messageQACandidates);
      Array.from(updatedMap.entries()).forEach(([messageId, candidate]) => {
        if (candidate.id === candidateId) {
          learnedQAService.getCandidate(clientId, candidateId).then((updated) => {
            if (updated) {
              updatedMap.set(messageId, updated);
              setMessageQACandidates(new Map(updatedMap));
            }
          }).catch((err) => {
            console.error('Error refreshing message candidate:', err);
          });
        }
      });
      setSelectedQACandidate(null);
      
      // Refresh Q&A candidates list
      if (selectedConversation) {
        try {
          const refreshedCandidates = await learnedQAService.getCandidates(clientId, 100, 0);
          const conversationCandidates = refreshedCandidates.filter(
            (c: any) => c.conversation_id === selectedConversation.conversation_id
          );
          setQaCandidates(conversationCandidates);
        } catch (err) {
          console.error('Error refreshing candidates:', err);
        }
      }
    } catch (error: any) {
      console.error('Error editing and approving candidate:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to edit and approve candidate';
      toast.error(typeof errorMessage === 'string' ? errorMessage : 'Failed to edit and approve candidate');
    }
  };

  const truncateTitle = (title: string, maxLength: number = 50) => {
    return title?.length > maxLength ? title.substring(0, maxLength) + '...' : title || 'Untitled';
  };

  // Extract Q&A handlers
  const handleExtractFromConversation = async () => {
    if (!clientId || !selectedConversation) return;
    
    try {
      setExtractingFromConversation(true);
      await learnedQAService.extractQAFromConversation(
        clientId,
        selectedConversation.conversation_id
      );
      toast.success('Q&A extracted from conversation successfully');
      
      // Refresh candidates list
      const candidates = await learnedQAService.getCandidates(clientId, 100, 0);
      const conversationCandidates = candidates.filter(
        (c: any) => c.conversation_id === selectedConversation.conversation_id
      );
      setQaCandidates(conversationCandidates);
    } catch (error: any) {
      console.error('Error extracting Q&A from conversation:', error);
      toast.error(error.message || 'Failed to extract Q&A from conversation');
    } finally {
      setExtractingFromConversation(false);
    }
  };

  const handleExtractFromMessage = async (messageId: string) => {
    if (!clientId || !selectedConversation) return;
    
    try {
      const loadingSet = new Set(extractingFromMessage);
      loadingSet.add(messageId);
      setExtractingFromMessage(loadingSet);
      
      await learnedQAService.extractQAFromConversation(
        clientId,
        selectedConversation.conversation_id,
        messageId
      );
      toast.success('Q&A extracted from message successfully');
      
      // Refresh candidates list
      const candidates = await learnedQAService.getCandidates(clientId, 100, 0);
      const conversationCandidates = candidates.filter(
        (c: any) => c.conversation_id === selectedConversation.conversation_id
      );
      setQaCandidates(conversationCandidates);
      
      // Refresh message-level candidates
      const candidate = await learnedQAService.getCandidateByMessageId(
        clientId,
        messageId,
        selectedConversation.conversation_id
      );
      if (candidate) {
        const updatedMap = new Map(messageQACandidates);
        updatedMap.set(messageId, candidate);
        setMessageQACandidates(updatedMap);
      }
    } catch (error: any) {
      console.error('Error extracting Q&A from message:', error);
      toast.error(error.message || 'Failed to extract Q&A from message');
    } finally {
      const loadingSet = new Set(extractingFromMessage);
      loadingSet.delete(messageId);
      setExtractingFromMessage(loadingSet);
    }
  };

  const groupMessagesIntoBlocks = (messages: ChatMessage[]) => {
    const blocks: Array<{
      id: string;
      userMessage: ChatMessage | null;
      assistantMessage: ChatMessage | null;
      timestamp: string;
    }> = [];

    const sortedMessages = [...messages].sort((a, b) => 
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );

    let currentBlock: {
      id: string;
      userMessage: ChatMessage | null;
      assistantMessage: ChatMessage | null;
      timestamp: string;
    } | null = null;

    for (const message of sortedMessages) {
      if (message.user_message && message.user_message.trim()) {
        if (currentBlock) {
          blocks.push(currentBlock);
        }
        currentBlock = {
          id: `block-${message.message_id}`,
          userMessage: message,
          assistantMessage: null,
          timestamp: message.created_at
        };
      }
      
      if (message.assistant_response && message.assistant_response.trim() && currentBlock) {
        currentBlock.assistantMessage = message;
        if (new Date(message.created_at) > new Date(currentBlock.timestamp)) {
          currentBlock.timestamp = message.created_at;
        }
      }
    }

    if (currentBlock) {
      blocks.push(currentBlock);
    }

    return blocks.reverse();
  };

  const showDeleteConfirmation = (messageId: string, conversationId: string, userId: string) => {
    setDeleteConfirmation({
      visible: true,
      messageId,
      conversationId,
      userId
    });
  };

  const handleDeleteMessage = async () => {
    if (!deleteConfirmation.messageId || !deleteConfirmation.conversationId || !deleteConfirmation.userId || !clientId) {
      return;
    }

    try {
      await deleteConversationMessage(
        clientId,
        deleteConfirmation.userId,
        deleteConfirmation.conversationId,
        deleteConfirmation.messageId
      );

      // If we get here, the delete was successful (axios throws on non-2xx)
      // Remove message from the messages list
      setMessages(prevMessages =>
        prevMessages.filter(msg => msg.message_id !== deleteConfirmation.messageId)
      );

      // Re-fetch conversations for this user to get updated counts
      try {
        const convResponse = await getUserConversations(clientId, deleteConfirmation.userId);
        const updatedUserConversations = convResponse.conversations || [];

        // Check if the conversation still exists (has non-deleted messages)
        const conversationStillExists = updatedUserConversations.find(
          (conv: any) => conv.conversation_id === deleteConfirmation.conversationId
        );

        if (!conversationStillExists) {
          // If conversation no longer exists, clear selection if it was selected
          if (selectedConversation?.conversation_id === deleteConfirmation.conversationId) {
            setSelectedConversation(null);
          }
        }

        // Update the grouped conversations with fresh data
        setGroupedConversations(prev => {
          const updated = { ...prev };
          const userId = deleteConfirmation.userId;

          if (userId && updated[userId]) {
            // Update conversations for this user
            updated[userId] = {
              ...updated[userId],
              conversations: updatedUserConversations.map((conv: any) => ({
                ...conv,
                user_id: userId,
                user_name: updated[userId].user_name,
                is_guest: userId.startsWith('guest_')
              })),
              total_messages: updatedUserConversations.reduce((sum: number, conv: any) => sum + (conv.message_count || 0), 0),
              total_feedback: updatedUserConversations.reduce((sum: number, conv: any) => sum + (conv.feedback_count || 0), 0)
            };

            // If user has no more conversations, remove them
            if (updated[userId].conversations.length === 0) {
              delete updated[userId];
            }
          }

          return updated;
        });
      } catch (error) {
        console.error('Error refreshing conversations:', error);
      }

      setDeleteConfirmation({
        visible: false,
        messageId: null,
        conversationId: null,
        userId: null
      });
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  // Filter and sort grouped conversations
  const filteredAndSortedGroups = Object.entries(groupedConversations)
    .filter(([, group]) => {
      // Search filter - search in both user names and conversation titles
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        
        // Check if user name matches
        const userNameMatch = group.display_name.toLowerCase().includes(searchLower);
        
        // Check if any conversation title matches
        const conversationMatch = group.conversations.some(conv => 
          conv.conversation_title?.toLowerCase().includes(searchLower)
        );
        
        // Return true if either user name or any conversation matches
        if (!userNameMatch && !conversationMatch) {
          return false;
        }
      }
      
      // User type filter
      if (userTypeFilter === 'guest' && !group.is_guest) return false;
      if (userTypeFilter === 'internal' && group.is_guest) return false;
      
      // Note: Feedback filtering is now applied at message level when displaying messages
      
      return true;
    })
    .sort(([, a], [, b]) => {
      switch (sortBy) {
        case 'message_count':
          return b.total_messages - a.total_messages;
        case 'feedback_count':
          return b.total_feedback - a.total_feedback;
        case 'last_activity':
        default:
          return new Date(b.last_activity).getTime() - new Date(a.last_activity).getTime();
      }
    });


  // Commented out - not used since backend doesn't provide response_time_ms
  // const formatResponseTime = (milliseconds: number): string => {
  //   if (!milliseconds || milliseconds < 0) return 'N/A';
  //   
  //   if (milliseconds < 1000) {
  //     return `${Math.round(milliseconds)}ms`;
  //   } else if (milliseconds < 60000) {
  //     return `${(milliseconds / 1000).toFixed(1)}s`;
  //   } else {
  //     return `${(milliseconds / 60000).toFixed(1)}min`;
  //   }
  // };



  

  // Toggle block collapse state
  const toggleBlockCollapse = (blockId: string) => {
    const newCollapsed = new Set(collapsedBlocks);
    if (newCollapsed.has(blockId)) {
      newCollapsed.delete(blockId);
    } else {
      newCollapsed.add(blockId);
    }
    setCollapsedBlocks(newCollapsed);
  };

  if (loading) {
    return (
      <div className="chat-history-container">
        <div className="loading">Loading conversation history...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chat-history-container">
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="chat-history-container">
      {/* Professional Filter Bar */}
      <div className="professional-filter-bar">
        <div className="filter-container">
          {/* Search with Icon */}
          <div className="search-with-icon">
            <svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
            </svg>
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="professional-search"
            />
            {searchTerm && (
              <button
                className="clear-search-btn"
                onClick={() => setSearchTerm('')}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>

          {/* Professional Select Filters */}
          <div className="filters-row">
            <div className="select-wrapper">
              <select
                value={userTypeFilter}
                onChange={(e) => setUserTypeFilter(e.target.value as any)}
                className="professional-select"
              >
                <option value="all">All Users</option>
                <option value="guest">Guest Users</option>
                <option value="internal">Internal Users</option>
              </select>
              <svg className="select-arrow" width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                <path d="M2 4l4 4 4-4"/>
              </svg>
            </div>

            <div className="select-wrapper">
              <select
                value={feedbackFilter}
                onChange={(e) => setFeedbackFilter(e.target.value as any)}
                className="professional-select"
              >
                <option value="all">All Feedback</option>
                <option value="thumbs_up">Positive</option>
                <option value="thumbs_down">Negative</option>
                <option value="need_review">Needs Review</option>
                <option value="no_feedback">No Feedback</option>
              </select>
              <svg className="select-arrow" width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                <path d="M2 4l4 4 4-4"/>
              </svg>
            </div>

            <div className="select-wrapper">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="professional-select"
              >
                <option value="last_activity">Recent Activity</option>
                <option value="message_count">Message Count</option>
                <option value="feedback_count">Feedback Count</option>
              </select>
              <svg className="select-arrow" width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                <path d="M2 4l4 4 4-4"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div className="main-content">
        {/* Conversations List */}
        <div className="conversations-sidebar enhanced">
          <div className="sidebar-header">
            <h3>Conversations ({filteredAndSortedGroups.length} users)</h3>
          </div>
          
          <div className="conversations-list">
            {filteredAndSortedGroups.map(([userId, group]) => (
              <div key={userId} className="user-group">
                <div className="user-group-header">
                  <div className="user-info">
                    <div className="user-name-container">
                      <span className="user-name-text">
                        {group.display_name}
                      </span>
                      {group.is_guest && group.preferences?.topics_of_interest?.length > 0 && (
                        <span 
                          className="user-preferences-indicator"
                          onMouseEnter={(e) => showPreferencesTooltip(e, group.preferences)}
                          onMouseLeave={hidePreferencesTooltip}
                        >
                          ?
                        </span>
                      )}
                    </div>
                    <span className={`user-type ${group.is_guest ? 'guest' : 'internal'}`}>
                      {group.is_guest ? 'User' : 'Internal'}
                    </span>
                  </div>
                  <div className="user-stats">
                    <span className="stat message-count">{group.total_messages} messages</span>
                    {group.total_feedback > 0 && (
                      <span className="stat feedback">{group.total_feedback} feedback</span>
                    )}
                  </div>
                </div>
                
                <div className="conversations-in-group">
                  {[...group.conversations]
                    .filter(conv => {
                      // If searching and user name doesn't match, only show conversations that match
                      if (searchTerm && !group.display_name.toLowerCase().includes(searchTerm.toLowerCase())) {
                        return conv.conversation_title?.toLowerCase().includes(searchTerm.toLowerCase());
                      }
                      return true;
                    })
                    .sort((a, b) => {
                      // Sort conversations within the group based on the selected sort option
                      switch (sortBy) {
                        case 'message_count':
                          return b.message_count - a.message_count;
                        case 'feedback_count':
                          return b.feedback_count - a.feedback_count;
                        case 'last_activity':
                        default:
                          return new Date(b.last_activity).getTime() - new Date(a.last_activity).getTime();
                      }
                    }).map((conversation) => (
                    <div
                      key={conversation.conversation_id}
                      className={`conversation-item ${selectedConversation?.conversation_id === conversation.conversation_id ? 'selected' : ''}`}
                      onClick={() => setSelectedConversation(conversation)}
                    >
                      <div className="conversation-title">
                        {truncateTitle(conversation.conversation_title)}
                      </div>
                      <div className="conversation-meta">
                        <span className="message-count">{conversation.message_count} msgs</span>
                        {conversation.feedback_count > 0 && (
                          <>
                            <span className="feedback-count">{conversation.feedback_count} feedback</span>
                            {qaStatusCounts.has(conversation.conversation_id) && (() => {
                              const counts = qaStatusCounts.get(conversation.conversation_id)!;
                              const total = counts.pending + counts.approved + counts.rejected;
                              if (total > 0) {
                                return (
                                  <span className="qa-status-counts">
                                    Pending ({counts.pending}) | Approved ({counts.approved}) | Rejected ({counts.rejected})
                                  </span>
                                );
                              }
                              return null;
                            })()}
                          </>
                        )}
                        {conversationRejections.has(conversation.conversation_id) && (() => {
                          const rejection = conversationRejections.get(conversation.conversation_id)!;
                          if (rejection.rejected) {
                            return (
                              <span className="conversation-rejection-badge" title={rejection.rejection_reason || 'Conversation rejected'}>
                                ❌ Rejected
                              </span>
                            );
                          }
                          return null;
                        })()}
                        <span className="conversation-date">{formatDate(conversation.last_activity)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          {/* Load More Button */}
          {hasMore && (
            <div className="load-more-users">
              <button 
                className="load-more-btn" 
                onClick={() => fetchConversations(currentOffset, true)}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </div>

        {/* Messages Panel */}
        <div className="messages-panel enhanced">
          {selectedConversation ? (
            <>
              {/* Review Feedback Button - Only show when conversation has feedback */}
              {selectedConversation && (() => {
                // Check if conversation has any messages with feedback (thumbs_up or need_review)
                const messagesWithFeedback = messages.filter(m =>
                  m.feedback_type === 'thumbs_up' || m.feedback_type === 'need_review'
                );

                // Only show button if there are messages with feedback
                if (messagesWithFeedback.length === 0) {
                  return null;
                }

                // Count candidates by status
                const pendingCount = qaCandidates.filter((c: any) =>
                  c.status === 'pending' &&
                  !['in_job', 'fine_tuned', 'approved'].includes(c.training_status)
                ).length;
                const approvedCount = qaCandidates.filter((c: any) => c.status === 'approved').length;
                const rejectedCount = qaCandidates.filter((c: any) => c.status === 'rejected').length;

                // Count assistant messages with feedback (thumbs_up or need_review)
                const assistantMessagesWithFeedback = messages.filter(m =>
                  m.assistant_response && m.assistant_response.trim() &&
                  (m.feedback_type === 'thumbs_up' || m.feedback_type === 'need_review')
                );
                const messagesWithCandidates = new Set(
                  qaCandidates.map((c: any) => {
                    const sourceMsgId = typeof c.context === 'object' && c.context
                      ? (c.context.source_message_id || c.context.message_id)
                      : null;
                    return sourceMsgId ? String(sourceMsgId) : null;
                  }).filter(Boolean)
                );
                const messagesWithFeedbackWithoutCandidates = assistantMessagesWithFeedback.filter(m =>
                  m.message_id && !messagesWithCandidates.has(String(m.message_id))
                ).length;

                // Total pending includes only messages with feedback that don't have candidates yet
                const totalPending = pendingCount + messagesWithFeedbackWithoutCandidates;

                return (
                  <div className="review-feedback-button-container">
                    <button
                      className="review-feedback-button"
                      onClick={() => setShowQAReviewModal(true)}
                    >
                      Review Feedback ({totalPending})
                    </button>
                    <span className="qa-status-summary" style={{
                      fontSize: '12px',
                      color: '#666',
                      marginLeft: '8px'
                    }}>
                      Pending: {totalPending} | Approved: {approvedCount} | Rejected: {rejectedCount}
                    </span>
                  </div>
                );
              })()}
              
              <div className="messages-list">
                {groupMessagesIntoBlocks(
                  messages.filter(message => {
                    // Apply feedback filter at message level
                    if (feedbackFilter === 'all') return true;
                    if (feedbackFilter === 'no_feedback') return !message.feedback_type;
                    if (feedbackFilter === 'thumbs_up') return message.feedback_type === 'thumbs_up';
                    if (feedbackFilter === 'thumbs_down') return message.feedback_type === 'thumbs_down';
                    if (feedbackFilter === 'need_review') return message.feedback_type === 'need_review';
                    return true;
                  })
                  // REMOVED search filter - we should show ALL messages in the selected conversation
                ).map((block, index, array) => {
                  const isCollapsed = collapsedBlocks.has(block.id);
                  const blockNumber = array.length - index;
                  
                  return (
                    <div key={block.id} className="conversation-block">
                      {/* Block Header - Always Visible */}
                      <div 
                        className="block-header"
                        onClick={() => toggleBlockCollapse(block.id)}
                      >
                        <div className="block-info">
                          <span className="block-number">#{blockNumber}</span>
                          <Tooltip
                            content={
                              <div style={{ padding: '8px 12px', fontSize: '13px', color: '#1f2937', lineHeight: '1.5' }}>
                                {block.userMessage?.user_message}
                              </div>
                            }
                            position="bottom"
                            trigger="hover"
                            showCloseButton={false}
                            arrow={true}
                            maxWidth="28rem"
                            className="block-preview-tooltip"
                            disabled={!block.userMessage?.user_message || block.userMessage.user_message.length <= 60}
                          >
                            <span className="block-preview">
                              {block.userMessage?.user_message}
                            </span>
                          </Tooltip>
                          
                          {/* Intent Information */}
                          {block.assistantMessage?.metadata?.intent && (() => {
                            const categoryDisplayNames: Record<string, string> = {
                              embeddings: 'knowledge',
                              knowledge_fallback: 'knowledge fallback',
                            };
                            const intent = block.assistantMessage.metadata.intent;
                            const intentName = Array.isArray(intent)
                              ? (intent[0] && typeof intent[0] === 'string' ? intent[0] : intent[0]?.intent || '')
                              : (typeof intent === 'string' ? intent : intent?.intent || '');
                            const displayName = categoryDisplayNames[intentName] ?? intentName.replace(/_/g, ' ');

                            if (intentName) {
                              return (
                                <span className="intent-badge" title={`Prompt: ${displayName}`}>
                                  <span className="intent-label">Prompt:</span>
                                  <span className="intent-name">{displayName}</span>
                                </span>
                              );
                            }
                            return null;
                          })()}
                          
                          {/* Action Buttons - Compact */}
                          <div className="message-actions-compact">
                            <button
                              className="action-btn copy-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(block.userMessage?.user_message || '', block.userMessage?.message_id || '');
                              }}
                              title="Copy query"
                            >
                              {copiedMessageId === block.userMessage?.message_id ? (
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                              ) : (
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                </svg>
                              )}
                            </button>
                            <button
                              className="action-btn delete-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (block.userMessage && selectedConversation) {
                                  showDeleteConfirmation(
                                    block.userMessage.message_id,
                                    block.userMessage.conversation_id,
                                    selectedConversation.user_id
                                  );
                                }
                              }}
                              title="Delete message"
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#dc3545" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                <line x1="14" y1="11" x2="14" y2="17"></line>
                              </svg>
                            </button>
                            {block.assistantMessage?.feedback_type && (
                              <span className="feedback-indicator-small" title={`Feedback: ${block.assistantMessage.feedback_type}`}>
                                {getFeedbackIcon(block.assistantMessage.feedback_type)}
                              </span>
                            )}
                          </div>
                          {/* Q&A Candidate Status and Actions */}
                          {block.assistantMessage?.feedback_type && 
                           ['thumbs_up', 'thumbs_down', 'need_review'].includes(block.assistantMessage.feedback_type) &&
                           block.assistantMessage?.message_id && (() => {
                            const candidate = messageQACandidates.get(block.assistantMessage.message_id);
                            const isLoading = loadingMessageQAs.has(block.assistantMessage.message_id);
                            
                            if (isLoading) {
                              return (
                                <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '8px' }}>
                                  Loading...
                                </span>
                              );
                            }
                            
                            if (candidate) {
                              const statusColors: { [key: string]: string } = {
                                'pending': '#f59e0b',
                                'approved': '#10b981',
                                'rejected': '#ef4444'
                              };
                              const statusLabels: { [key: string]: string } = {
                                'pending': 'Pending Review',
                                'approved': 'Approved',
                                'rejected': 'Rejected'
                              };
                              
                              return (
                                <div style={{ 
                                  display: 'inline-flex', 
                                  alignItems: 'center', 
                                  gap: '8px', 
                                  marginLeft: '8px',
                                  padding: '4px 8px',
                                  background: '#f3f4f6',
                                  borderRadius: '4px',
                                  fontSize: '12px'
                                }}>
                                  <span style={{
                                    color: statusColors[candidate.status] || '#6b7280',
                                    fontWeight: 500
                                  }}>
                                    {statusLabels[candidate.status] || candidate.status}
                                  </span>
                                  {/* Action buttons removed - use Review Feedback modal instead */}
                                </div>
                              );
                            }
                            
                            return null;
                          })()}
                        </div>
                        <div className="block-controls">
                          <span className="block-timestamp">{formatDate(block.timestamp)}</span>
                          <span className={`collapse-icon ${isCollapsed ? 'collapsed' : 'expanded'}`}>
                            {isCollapsed ? '▶' : '▼'}
                          </span>
                        </div>
                      </div>

                      {/* Block Content - Collapsible */}
                      {!isCollapsed && (
                        <div className="block-content">
                          {/* Show conversation rejection status if conversation was rejected */}
                          {selectedConversation && conversationRejections.has(selectedConversation.conversation_id) && (() => {
                            const rejection = conversationRejections.get(selectedConversation.conversation_id)!;
                            if (rejection.rejected) {
                              return (
                                <div className="conversation-rejection-notice">
                                  <div className="rejection-notice-header">
                                    <span className="rejection-icon">❌</span>
                                    <span className="rejection-label">Conversation Rejected:</span>
                                  </div>
                                  <div className="rejection-notice-content">
                                    {rejection.rejection_reason || 'No reason provided'}
                                  </div>
                                  {rejection.rejected_by && (
                                    <div className="rejection-notice-meta">
                                      Rejected by: {rejection.rejected_by} {rejection.rejected_at ? `on ${new Date(rejection.rejected_at).toLocaleDateString()}` : ''}
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            return null;
                          })()}
                          
                          {/* Show feedback reason for thumbs down at the top */}
                          {block.assistantMessage?.feedback_type === 'thumbs_down' && block.assistantMessage?.feedback_reason && (
                            <div className="feedback-reason">
                              <div className="feedback-reason-header">
                                <span className="feedback-icon">👎</span>
                                <span className="feedback-label">Feedback Reason:</span>
                              </div>
                              <div className="feedback-reason-content">
                                {block.assistantMessage.feedback_reason}
                              </div>
                              {block.assistantMessage.feedback_comments && (
                                <div className="feedback-comments">
                                  <strong>Additional Comments:</strong> {block.assistantMessage.feedback_comments}
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Response Content using shared MessageRenderer */}
                          {block.assistantMessage && (
                            <MessageRenderer
                              content={block.assistantMessage.assistant_response || ''}
                              messageId={block.assistantMessage.message_id}
                              className=""
                              citationSources={block.assistantMessage.metadata?.citation_sources || block.assistantMessage.metadata?.citations || []}
                            />
                          )}

                          {/* Footer Actions removed - use Review Feedback modal instead */}
                          
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

            </>
          ) : (
            <div className="no-conversation-selected">
              <h3>Select a conversation</h3>
              <p>Choose a conversation from the left to view messages and feedback</p>
              <div className="stats-summary">
                <div className="stat-item">
                  <span className="stat-number">{Object.keys(groupedConversations).length}</span>
                  <span className="stat-label">Total Users</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">
                    {Object.values(groupedConversations).reduce((sum, group) => sum + group.conversations.length, 0)}
                  </span>
                  <span className="stat-label">Total Conversations</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">
                    {Object.values(groupedConversations).reduce((sum, group) => sum + group.total_messages, 0)}
                  </span>
                  <span className="stat-label">Total Messages</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">
                    {Object.values(groupedConversations).reduce((sum, group) => sum + group.total_feedback, 0)}
                  </span>
                  <span className="stat-label">Total Feedback</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Citation Tooltip using modular component */}
      <CitationTooltip
        visible={citationTooltip.visible}
        title={citationTooltip.title}
        url={citationTooltip.url}
        content={citationTooltip.content}
        x={citationTooltip.x}
        y={citationTooltip.y}
        position={citationTooltip.position}
        horizontalPosition={citationTooltip.horizontalPosition}
        sourceType={citationTooltip.sourceType}
        theme="light"
        onMouseEnter={tooltipEventHandlers.onMouseEnter}
        onMouseLeave={tooltipEventHandlers.onMouseLeave}
      />
      
      {/* User Preferences Tooltip */}
      {preferencesTooltip.visible && preferencesTooltip.preferences && (
        <div 
          className="preferences-tooltip"
          style={{
            position: 'fixed',
            left: preferencesTooltip.x + 'px',
            top: preferencesTooltip.y + 'px',
            zIndex: 1000
          }}
          onMouseEnter={cancelHideTooltip}
          onMouseLeave={hidePreferencesTooltip}
        >
          <div className="preferences-tooltip-content">
            <h4>Topics of Interest</h4>
            <div className="topics-list">
              {preferencesTooltip.preferences.topics_of_interest?.map((topic: string, index: number) => (
                <span key={index} className="topic-tag">{topic}</span>
              ))}
            </div>
            {preferencesTooltip.preferences.interaction_style && (
              <div className="preference-item">
                <strong>Interaction Style:</strong> {preferencesTooltip.preferences.interaction_style}
              </div>
            )}
            {preferencesTooltip.preferences.conversation_count > 0 && (
              <div className="preference-item">
                <strong>Conversations:</strong> {preferencesTooltip.preferences.conversation_count}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {deleteConfirmation.visible && (
        <div className="delete-modal-overlay" onClick={() => setDeleteConfirmation({ visible: false, messageId: null, conversationId: null, userId: null })}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 6h18"></path>
                <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
              </svg>
            </div>
            <h3 className="delete-modal-title">Delete Message</h3>
            <p className="delete-modal-text">
              Are you sure you want to permanently delete this message? This action cannot be undone.
            </p>
            <div className="delete-modal-actions">
              <button 
                className="delete-modal-cancel"
                onClick={() => setDeleteConfirmation({ visible: false, messageId: null, conversationId: null, userId: null })}
              >
                Cancel
              </button>
              <button 
                className="delete-modal-confirm"
                onClick={handleDeleteMessage}
              >
                Delete Message
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Q&A Review Modal */}
      {selectedConversation && (
        <QAReviewModal
          isOpen={showQAReviewModal}
          onClose={() => setShowQAReviewModal(false)}
          candidates={qaCandidates}
          messages={messages}
          conversationId={selectedConversation.conversation_id}
          onApprove={handleApproveQA}
          onReject={handleRejectQA}
          onEditAndApprove={handleEditAndApproveQA}
          onRefresh={async () => {
            // Refresh candidates after removal from training
            if (clientId && selectedConversation) {
              try {
                const candidates = await learnedQAService.getCandidates(clientId, 1000, 0);
                const conversationCandidates = candidates.filter(
                  (c: any) => c.conversation_id === selectedConversation.conversation_id
                );
                setQaCandidates(conversationCandidates);
                
                // Also update message-level candidates
                const updatedMap = new Map(messageQACandidates);
                conversationCandidates.forEach((candidate: any) => {
                  const sourceMsgId = typeof candidate.context === 'object' && candidate.context 
                    ? (candidate.context.source_message_id || candidate.context.message_id)
                    : null;
                  if (sourceMsgId) {
                    updatedMap.set(String(sourceMsgId), candidate);
                  }
                });
                setMessageQACandidates(updatedMap);
                
                // Update status counts
                const statusCountsMap = new Map<string, {pending: number, approved: number, rejected: number}>();
                candidates.forEach((candidate: any) => {
                  const convId = candidate.conversation_id;
                  if (!statusCountsMap.has(convId)) {
                    statusCountsMap.set(convId, { pending: 0, approved: 0, rejected: 0 });
                  }
                  const counts = statusCountsMap.get(convId)!;
                  if (candidate.status === 'pending') counts.pending++;
                  else if (candidate.status === 'approved') counts.approved++;
                  else if (candidate.status === 'rejected') counts.rejected++;
                });
                setQaStatusCounts(statusCountsMap);
              } catch (error) {
                console.error('Error refreshing candidates:', error);
              }
            }
          }}
          filter={qaFilter}
          onFilterChange={setQaFilter}
          loading={loadingQACandidates}
          onExtractFromConversation={handleExtractFromConversation}
          onExtractFromMessage={handleExtractFromMessage}
          extractingFromConversation={extractingFromConversation}
          extractingFromMessage={extractingFromMessage}
        />
      )}
    </div>
  );
};

export default ChatHistory;